import { apiClient } from './apiClient';
import { requestCache } from '../utils/requestCache';

/**
 * Creator/Designer Service
 * Handles public creator profile operations
 */

const CREATOR_CACHE_PREFIX = 'creator_';
const creatorCacheKey = (identifier: string) => `${CREATOR_CACHE_PREFIX}${identifier}`;

export interface SocialLinks {
  instagram?: string;
  facebook?: string;
  twitter?: string;
  tiktok?: string;
  linkedin?: string;
  website?: string;
  whatsapp_number?: string;
}

export interface Creator {
  id: string;
  name: string;
  brand_name?: string;
  username?: string;
  fullName?: string;
  role: string;
  company: string;
  location: string;
  countryFlag: string;
  specialties: string[];
  sessions: number;
  reviews: number;
  experience: number;
  attendance: number;
  isAvailable: boolean;
  isVerified: boolean;
  profilePicture: string;
  coverImage?: string;
  bio?: string;
  isPremium: boolean;
  rating: number;
  reviewCount: number;
  portfolioImages: string[];
  socialMedia: SocialLinks;
  availability: string[];
  /** Account type as returned by `/users/creators/:id`. Buyers have profiles
   *  but no creator-only surfaces (chat panel, archive, store). When absent
   *  (legacy responses), treat as a creator/individual. */
  accountType?: 'creator' | 'individual' | 'buyer';
  /** Set to true by the frontend when the creator object is a placeholder
   *  built from `createFallbackCreator` after a failed API fetch. Real
   *  creator records from the API never carry this field. */
  _isFallback?: boolean;
  payoutAccount?: {
    id: string;
    accountHolderName: string;
    bankName: string;
    accountNumber: string;
    currency: string;
    country: string;
    isDefault: boolean;
    isVerified: boolean;
  };
}

export interface CreatorListParams {
  page?: number;
  limit?: number;
  search?: string;
  country?: string;
  location?: string;
  specialty?: string;
  sortBy?: 'featured' | 'rating' | 'experience' | 'availability';
  verified?: boolean;
  available?: boolean;
}

export interface CreatorListResponse {
  items: Creator[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const creatorService = {
  /**
   * Get paginated list of all creators/designers
   * GET /users/creators
   */
  getCreators: async (params?: CreatorListParams): Promise<CreatorListResponse> => {
    const queryParams = new URLSearchParams();
    
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.country) queryParams.append('country', params.country);
    if (params?.location) queryParams.append('location', params.location);
    if (params?.specialty) queryParams.append('specialty', params.specialty);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.verified !== undefined) queryParams.append('verified', params.verified.toString());
    if (params?.available !== undefined) queryParams.append('available', params.available.toString());

    const queryString = queryParams.toString();
    const url = `/users/creators${queryString ? `?${queryString}` : ''}`;
    
    const response = await apiClient.get<CreatorListResponse>(url);
    
    if (!response.success) {
      throw new Error(response.error.message);
    }
    
    return response.data;
  },

  /**
   * Get detailed profile of a specific creator
   * GET /users/creators/:identifier (accepts UUID or username)
   *
   * Cached via requestCache (5 min TTL) to avoid re-fetching the same
   * profile on every navigation. Use `invalidateCreator()` after a
   * mutation to force the next call to hit the network.
   */
  getCreatorById: async (identifier: string): Promise<Creator> => {
    const key = creatorCacheKey(identifier);
    try {
      return await requestCache.get(key, async () => {
        const response = await apiClient.get<Creator>(`/users/creators/${identifier}`);
        if (!response.success) {
          throw new Error(response.error.message);
        }
        return response.data;
      });
    } catch (err) {
      // Don't keep a failed request in cache — let the next call retry.
      requestCache.delete(key);
      throw err;
    }
  },

  /**
   * Invalidate cached creator profile(s).
   * - Pass an identifier (username or UUID) to clear a specific entry.
   * - Pass nothing to clear every cached creator profile.
   */
  invalidateCreator: (identifier?: string) => {
    if (identifier) {
      requestCache.delete(creatorCacheKey(identifier));
    } else {
      requestCache.deleteByPrefix(CREATOR_CACHE_PREFIX);
    }
  },

  /**
   * Warm the creator-profile cache for the given identifier without
   * waiting on (or surfacing) the result. Use this on pages where the
   * user is likely to navigate to the creator's profile next (e.g. an
   * archive post detail page) so the eventual navigation hits the
   * cache instead of triggering a fresh network request.
   *
   * Safe to call repeatedly — duplicate in-flight requests are
   * deduped by `requestCache` and resolved profiles are served from
   * cache for 5 minutes.
   */
  prefetchCreator: (identifier?: string | null) => {
    if (!identifier) return;
    void creatorService.getCreatorById(identifier).catch(() => {
      // Swallow errors — the social profile page handles its own
      // failure flow when the user actually navigates there.
    });
  },
};
